import React from 'react';
import { View, StyleSheet, Text, TouchableOpacity, ScrollView } from 'react-native';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { ResidentDetailsHeader, ResidentDetailsInfo, ResidentEditForm, LoadingSpinner } from '@/components';
import { useResidentDetails } from '@/hooks/admin/useResidentDetails';
import { ADMIN_COLORS } from '@/constants/admin';
import AdminErrorState from '@/components/common/admin/mobile/AdminErrorState';
import { DesktopPanelHeader } from '@/components/common/admin/desktop/panel/DesktopPanelHeader';
import { DesktopPanelTabs } from '@/components/common/admin/desktop/panel/DesktopPanelTabs';
import { DesktopPanelBody } from '@/components/common/admin/desktop/panel/DesktopPanelBody';
import { RESIDENT_DETAILS_TABS } from '@/constants/admin/residents';

interface ResidentDetailsSidePanelProps {
	visible: boolean;
	onClose: () => void;
	accountId: number | null;
	leaseId: number | null;
  mode?: 'account' | 'resident';
}

export const ResidentDetailsSidePanel: React.FC<ResidentDetailsSidePanelProps> = ({
	visible,
	onClose,
	accountId,
	leaseId,
  mode = 'resident',
}) => {
	const hasParams = !!accountId && !!leaseId;
	const { residentDetails, isLoading, error, enhancedError, refetch, retryCount, canRetry } = useResidentDetails(
		hasParams ? { accountId: accountId as number, leaseId: leaseId as number, mode } : ({ accountId: 0, leaseId: 0, mode } as any),
	);

	const tabs = [RESIDENT_DETAILS_TABS.GENERAL, RESIDENT_DETAILS_TABS.EDIT];
	const [activeTab, setActiveTab] = React.useState(tabs[0]);

	return (
		<DesktopSidePanel visible={visible} onClose={onClose} width={520}>
			<View style={styles.container}>
				<DesktopPanelHeader title="Resident Details" onClose={onClose} />
				{!hasParams ? null : isLoading && !residentDetails ? (
					<LoadingSpinner message="Loading resident details..." />
				) : error && !residentDetails ? (
					<AdminErrorState
						title="Unable to Load Resident Details"
						message={enhancedError?.userFriendlyMessage || 'Something went wrong while loading the resident details.'}
						retryCount={retryCount}
						canRetry={canRetry}
						onRetry={refetch}
					/>
				) : residentDetails ? (
					<>
						<ResidentDetailsHeader resident={residentDetails} onClose={onClose} showClose={false} />
						<DesktopPanelTabs tabs={tabs} activeIndex={tabs.indexOf(activeTab)} onChange={(i) => setActiveTab(tabs[i])} />

					{activeTab === RESIDENT_DETAILS_TABS.GENERAL ? (
						<DesktopPanelBody scroll>
							<ResidentDetailsInfo resident={residentDetails} leaseCustomerId={accountId as number} leaseId={leaseId as number} />
						</DesktopPanelBody>
						) : (
							<DesktopPanelBody>
								<ResidentEditForm accountId={accountId as number} residentDetails={residentDetails} />
							</DesktopPanelBody>
						)}

						{/* Loading overlay during refetches to prevent showing stale content */}
						{isLoading && (
							<View style={styles.loadingOverlay} pointerEvents="none">
								<LoadingSpinner message="Refreshing..." />
							</View>
						)}
					</>
				) : null}
			</View>
		</DesktopSidePanel>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
		position: 'relative',
	},
	tabContainer: {
		backgroundColor: ADMIN_COLORS.SURFACE,
		borderBottomWidth: 1,
		borderBottomColor: ADMIN_COLORS.BORDER,
		paddingVertical: 12,
		paddingHorizontal: 16,
		flexDirection: 'row',
		gap: 12,
	},
	tab: {
		flex: 1,
		paddingVertical: 8,
		borderRadius: 20,
		backgroundColor: 'transparent',
		alignItems: 'center',
	},
	activeTab: {
		backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
	},
	tabText: {
		fontSize: 14,
		fontWeight: '500',
		color: ADMIN_COLORS.TEXT_SECONDARY,
	},
	activeTabText: {
		color: '#FFFFFF',
	},
	contentContainer: {
		flex: 1,
		paddingHorizontal: 16,
		paddingBottom: 16,
	},
	loadingOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: 'rgba(0,0,0,0.0)',
	},
});
